import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    fetchArticlesByCategory,
    resetFetchedCategory,
    markCategoryFetched,
    setPage,
} from '../slices/siteArticlesSlice.mjs';
import { fetchBulkReactions } from '../slices/reactionsSlice.mjs';
import { fetchBulkRatingAggregates } from '../slices/ratingsSlice.mjs';
import { setPageTitle, setPageDescription, setPageMetadata, setCanonicalUrl, resetPageMetadata } from '../slices/pageMetadataSlice.mjs';

import HomePageContent from './body/Home.mjs';
import useIsMounted from '../hooks/useIsMounted.mjs';
import { setHeaderData } from '../slices/headerSlice.mjs';
import useSafeSearchParams from '../hooks/useSafeSearchParams.mjs';

// Client-side only component to detect route parameters
const CategoryRouteHandler = ({ onCategoryChange }) => {
    const params = useParams();

    useEffect(() => {
        if (params.categoryId) {
            onCategoryChange(params.categoryId);
        }
    }, [params.categoryId, onCategoryChange]);

    return null;
};



const CategoryPageWrapper = () => {
    const {
        articlesByCategory,
        isLoadingByCategory,
        fetchParams,
        fetchedCategories,
        pagination,
    } = useSelector(
        (state) =>
            state.siteArticles || {
                articlesByCategory: [],
                isLoadingByCategory: false,
                fetchParams: {},
                fetchedCategories: {},
                pagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
            }
    );

    // Get layout settings from Redux (set by middleware)
    const layoutSettings = useSelector((state) => state.pageLayoutSettings?.current);
    const articlesPerPage = layoutSettings?.pagination?.articlesPerPage || 25;

    // ADD REACTIONS STATE TO SELECTOR
    const {
        byArticleId: reactionsByArticleId,
        loading: reactionsLoading,
        fetched: reactionsFetched,
    } = useSelector(
        (state) =>
            state.reactions || {
                byArticleId: {},
                loading: { bulk: false },
                fetched: {},
            }
    );

    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const [searchParams, setSearchParams] = useSafeSearchParams();
    const [currentCategory, setCurrentCategory] = React.useState(null);
    const currentPage = pagination?.page || 1;

    const handleCategoryChange = React.useCallback((categoryId) => {
        console.log(`Category changed to: ${categoryId}`);
        setCurrentCategory(categoryId);
    }, []);

    // ADD ARTICLE IDS EXTRACTION FOR CURRENT CATEGORY
    const currentCategoryArticleIds = useMemo(() => {
        if (!currentCategory || !articlesByCategory || articlesByCategory.length === 0) {
            return [];
        }
        const categoryGroup = articlesByCategory.find(
            (group) => group.category === currentCategory
        );
        return categoryGroup && Array.isArray(categoryGroup.articles)
            ? categoryGroup.articles.map((article) => article?._id).filter(Boolean)
            : [];
    }, [articlesByCategory, currentCategory]);

    // ADD UNFETCHED REACTION IDS LOGIC
    const unfetchedReactionIds = useMemo(() => {
        return currentCategoryArticleIds.filter(
            (articleId) => !reactionsFetched[articleId] && !reactionsByArticleId[articleId]
        );
    }, [currentCategoryArticleIds, reactionsFetched, reactionsByArticleId]);

    // Effect for handling metadata and initial fetch for a category
    useEffect(() => {
        if (!currentCategory) return;

        // Set category-specific metadata
        const categoryName = currentCategory.charAt(0).toUpperCase() + currentCategory.slice(1);
        const pageTitle = `Nexus of Truth / ${categoryName}`;
        const pageDesc = `Explore articles about ${categoryName}.`;
        dispatch(setPageTitle(pageTitle));
        dispatch(setPageDescription(pageDesc));
        const canonicalUrl = typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}/${currentCategory}` : '';
        dispatch(setCanonicalUrl(canonicalUrl));
        // Extract first available article image for social cards
        let firstImage = null;
        if (Array.isArray(articlesByCategory)) {
            for (const group of articlesByCategory) {
                if (group?.articles?.length) {
                    for (const a of group.articles) {
                        const img = a?.generatedImages?.[0]?.url || a?.images?.[0]?.url;
                        if (img) { firstImage = img; break; }
                    }
                }
                if (firstImage) break;
            }
        }
        dispatch(setPageMetadata({
            'og:title': pageTitle,
            'og:description': pageDesc,
            'og:type': 'website',
            'og:url': canonicalUrl,
            ...(firstImage ? { 'og:image': firstImage } : {}),
            'twitter:card': firstImage ? 'summary_large_image' : 'summary',
            'twitter:title': pageTitle,
            'twitter:description': pageDesc,
            ...(firstImage ? { 'twitter:image': firstImage } : {}),
        }));
        dispatch(
            setHeaderData({
                title: 'Nexus of Truth',
                description: 'Discover the truth through articles and insights.',
            })
        );

        // Check if we should reset the category fetch tracking (for forced refreshes)
        const forceRefresh = false; // You could add UI to allow users to force refresh

        if (forceRefresh) {
            dispatch(resetFetchedCategory(currentCategory));
        }

        // Check if this category's data exists with articles
        const categoryDataInStore = articlesByCategory.find(
            (group) => group.category === currentCategory
        );
        const categoryHasArticles =
            categoryDataInStore &&
            categoryDataInStore.articles &&
            categoryDataInStore.articles.length > 0;

        // Check if we've already fetched this category using Redux state
        const alreadyFetchedThisCategory = !!fetchedCategories[currentCategory]?.fetched;
        const fetchIsStillPending = !!fetchedCategories[currentCategory]?.pending;

        // Determine what action to take
        if (!categoryHasArticles && !alreadyFetchedThisCategory) {
            console.log(`CategoryPage: FETCHING articles for "${currentCategory}".`);

            // Dispatch the fetch - Redux will track this category as fetched
            dispatch(
                fetchArticlesByCategory({
                    queryType: 'byCategory',
                    category: currentCategory,
                    limitPerCategory: articlesPerPage,
                })
            );
        } else if (!categoryHasArticles && alreadyFetchedThisCategory && fetchIsStillPending) {
            console.log(
                `CategoryPage: WAITING for articles for "${currentCategory}" (fetch in progress).`
            );
        } else if (!categoryHasArticles && alreadyFetchedThisCategory && !fetchIsStillPending) {
            console.log(
                `CategoryPage: NO ARTICLES available for "${currentCategory}" (fetch completed).`
            );
        } else {
            console.log(`CategoryPage: USING existing articles for "${currentCategory}".`);

            // If we have articles but the category isn't marked as fetched (could happen if Redux state partially resets)
            // Go ahead and mark it as fetched to keep state consistent
            if (!alreadyFetchedThisCategory && categoryHasArticles) {
                dispatch(markCategoryFetched({ category: currentCategory, success: true }));
            }

            // DON'T dispatch bulk reactions here - let the dedicated useEffect handle it
        }

        return () => {
            dispatch(resetPageMetadata());
        };
    }, [currentCategory, dispatch, articlesByCategory, fetchedCategories]);

    // ADD SMART BULK REACTIONS FETCHING
    useEffect(() => {
        // Only fetch if:
        // 1. We have unfetched article IDs
        // 2. We're not already loading bulk reactions
        // 3. We're not currently fetching articles (wait for articles to complete first)
        const shouldFetchReactions =
            unfetchedReactionIds.length > 0 && !reactionsLoading.bulk && !isLoadingByCategory;

        if (shouldFetchReactions) {
            console.log(
                `CategoryPage: Loading reactions for ${unfetchedReactionIds.length} new articles`
            );
            dispatch(fetchBulkReactions({ articleIds: unfetchedReactionIds }));
        }
    }, [unfetchedReactionIds, dispatch, reactionsLoading.bulk, isLoadingByCategory]);

    // Rating aggregates — separate effect so reaction state changes don't re-trigger
    useEffect(() => {
        if (currentCategoryArticleIds.length > 0 && !isLoadingByCategory) {
            dispatch(fetchBulkRatingAggregates({ articleIds: currentCategoryArticleIds }));
        }
    }, [currentCategoryArticleIds, dispatch, isLoadingByCategory]);

    // Filter articles to pass to HomePageContent
    const articlesForCurrentCategory = useMemo(() => {
        if (!currentCategory || !articlesByCategory || articlesByCategory.length === 0) {
            return [];
        }
        const categoryGroup = articlesByCategory.find(
            (group) => group.category === currentCategory
        );
        return categoryGroup ? [categoryGroup] : [];
    }, [articlesByCategory, currentCategory]);

    // Determine if the current category's articles are effectively empty
    const noArticlesForDisplay =
        !articlesForCurrentCategory[0] || articlesForCurrentCategory[0]?.articles?.length === 0;

    // Show loading spinner only if actively loading for current category and no articles to display
    const showLoading =
        isLoadingByCategory && fetchParams?.category === currentCategory && noArticlesForDisplay;

    // Sync URL page to Redux on mount and when URL changes (back/forward)
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    useEffect(() => {
        if (isMounted && urlPage !== currentPage) {
            dispatch(setPage(urlPage));
            dispatch(resetFetchedCategory(currentCategory));
            dispatch(
                fetchArticlesByCategory({
                    queryType: 'byCategory',
                    category: currentCategory,
                    page: urlPage,
                    limitPerCategory: articlesPerPage,
                })
            );
        }
    }, [urlPage]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle page change - update URL (enables browser history)
    const handlePageChange = useCallback((newPage, updateUrl = true) => {
        if (updateUrl) {
            setSearchParams({ page: newPage.toString() });
        }
        dispatch(setPage(newPage));
        dispatch(resetFetchedCategory(currentCategory));
        dispatch(
            fetchArticlesByCategory({
                queryType: 'byCategory',
                category: currentCategory,
                page: newPage,
                limitPerCategory: articlesPerPage,
            })
        );
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [setSearchParams, dispatch, currentCategory, articlesPerPage]);

    return (
        <>
            {isMounted && <CategoryRouteHandler onCategoryChange={handleCategoryChange} />}

            <main className="home-page-container">
                {showLoading ? (
                    <div className="home-loading-container">
                        <div className="home-loading-spinner"></div>
                        <p>Loading articles for {currentCategory || 'selected category'}...</p>
                    </div>
                ) : noArticlesForDisplay && currentCategory ? (
                    <div className="home-no-articles-message">
                        <h2>
                            No articles available for {currentCategory || 'the selected category'}
                        </h2>
                        <p>Check back soon for the latest content.</p>
                    </div>
                ) : (
                    <HomePageContent
                        articlesGroupedByCategory={articlesForCurrentCategory}
                        pagination={pagination}
                        onPageChange={handlePageChange}
                    />
                )}
            </main>
        </>
    );
};

export default CategoryPageWrapper;
